import React from 'react';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { formatDateOnly } from '../../../../../utils/dateUtils';
import { useGetWarrantyAuditHistoryQuery } from '../../../../../api/endpoints/warrantyAudit';
import useWarrantyAuditPdf from '../hooks/useWarrantyAuditPdf';

const cellStyle = { border: '1px solid var(--surface-border)', padding: '6px 8px', textAlign: 'center' };
const headerStyle = { ...cellStyle, backgroundColor: 'var(--surface-100)', fontWeight: 600 };

const HistoryMatrix = ({ bosBranchCode }) => {
    const { th, getCurrentLanguageId } = useLanguage();
    const languageId = getCurrentLanguageId();
    const { generate, isGenerating } = useWarrantyAuditPdf();

    const { data, isLoading, isFetching } = useGetWarrantyAuditHistoryQuery(
        { bosBranchCode, bosBranchSiteCode: bosBranchCode, languageId },
        { skip: !bosBranchCode }
    );

    if (!bosBranchCode) return null;

    if (isLoading || isFetching) {
        return (
            <div className="flex justify-content-center p-4">
                <ProgressSpinner style={{ width: '40px', height: '40px' }} />
            </div>
        );
    }

    const audits = data?.audits || [];
    const rows = data?.rows || [];
    const totals = data?.totals || [];

    if (audits.length === 0 || rows.length === 0) {
        return <p className="text-color-secondary">{th('warrantyAudit.history.empty') || 'Geçmiş denetim kaydı bulunmuyor.'}</p>;
    }

    const totalByAudit = totals.reduce((acc, t) => {
        acc[t.auditId] = t;
        return acc;
    }, {});

    return (
        <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead>
                    <tr>
                        <th style={{ ...headerStyle, textAlign: 'left' }}>{th('warrantyAudit.fields.subCriterion') || 'Alt Kriter'}</th>
                        {audits.map((a) => {
                            const dateText = a.auditDate ? formatDateOnly(a.auditDate) : '';
                            return (
                                <th key={a.auditId} style={headerStyle} title={a.auditDate}>
                                    {a.label}
                                    {dateText && <div style={{ fontWeight: 400, fontSize: '0.8rem' }}>({dateText})</div>}
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.subCriteriaId}>
                            <td style={{ ...cellStyle, textAlign: 'left' }}>
                                {row.displayCode ? `${row.displayCode} ` : ''}
                                {row.subCriteriaName}
                            </td>
                            {audits.map((a) => (
                                <td key={a.auditId} style={cellStyle}>
                                    {row.scores?.[a.auditId] != null ? row.scores[a.auditId] : '-'}
                                </td>
                            ))}
                        </tr>
                    ))}
                    <tr>
                        <td style={{ ...headerStyle, textAlign: 'left' }}>{th('warrantyAudit.fields.totalScore') || 'Toplam Puan'}</td>
                        {audits.map((a) => (
                            <td key={a.auditId} style={{ ...cellStyle, fontWeight: 600 }}>
                                {totalByAudit[a.auditId]?.totalScore ?? '-'}
                            </td>
                        ))}
                    </tr>
                    <tr>
                        <td style={{ ...headerStyle, textAlign: 'left' }}>{th('warrantyAudit.fields.result') || 'Sonuç'}</td>
                        {audits.map((a) => {
                            const t = totalByAudit[a.auditId];
                            return (
                                <td key={a.auditId} style={cellStyle}>
                                    {t ? (
                                        <span
                                            style={{
                                                backgroundColor: t.scoreBandColor || '#64748b',
                                                color: '#fff',
                                                padding: '2px 8px',
                                                borderRadius: '4px',
                                                fontSize: '0.8rem',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {t.scoreBandText}
                                        </span>
                                    ) : (
                                        '-'
                                    )}
                                </td>
                            );
                        })}
                    </tr>
                    <tr>
                        <td style={{ ...headerStyle, textAlign: 'left' }}>{th('warrantyAudit.actions.pdf') || 'PDF'}</td>
                        {audits.map((a) => (
                            <td key={a.auditId} style={cellStyle}>
                                <Button
                                    icon="pi pi-file-pdf"
                                    rounded
                                    text
                                    severity="danger"
                                    tooltip={th('warrantyAudit.actions.pdf') || 'PDF'}
                                    loading={isGenerating}
                                    onClick={() => generate(a.auditId, { download: false })}
                                />
                            </td>
                        ))}
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

export default HistoryMatrix;
